import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import * as React from 'react'
import type mapboxgl from 'mapbox-gl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'lib/routing/appRouting'
import type { Location } from 'history'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LayoutContainer from 'components/Common/LayoutContainer'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import IconButton from 'LuxKit/Button/IconButton'
import LineExpandAltIcon from 'LuxKit/Icons/line/LineExpandAltIcon'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import SearchResultsMap, { type HoveredOffer } from 'components/Search/SearchResultsMap/SearchResultsMap'
import { isAnyOffer } from 'lib/offer/offerTypes'
import { decodeOfferIds, encodeOfferIds, encodeSearchParams } from 'lib/search/searchUtils'
import { serialiseMapBounds } from 'lib/maps/bounds'
import type { MapBounds } from 'lib/maps/types'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { replaceWithRegion } from 'actions/NavigationActions'
import noop from 'lib/function/noop'

const SplitListPane = styled(VerticalSpacer)`
  min-width: 0;
`

const SplitViewContainer = styled(LayoutContainer)`
  display: grid;
  grid-template-columns: minmax(0, ${rem(811)}) minmax(${rem(360)}, 1fr);
  gap: ${rem(16)};
  min-height: calc(100vh - ${rem(220)});
  position: relative;
  overflow: visible;
  padding-left: ${rem(16)};
  padding-right: 0;
  align-items: start;

  &.hideListView {
    grid-template-columns: 1fr;
    gap: 0;

    > ${SplitListPane} {
      display: none;
    }
  }
`

const SplitMapPane = styled.div`
  position: sticky;
  top: calc(${props => props.theme.layout.headerFloatingOffset} + var(--sticky-top-offset, 0px));
  height: calc((var(--vh, 1vh) * 100) - ${props => props.theme.layout.headerFloatingOffset} - var(--sticky-top-offset, 0px));
  overflow: hidden;
`

const MapControlsContainer = styled.div`
  position: absolute;
  top: ${rem(10)};
  right: ${rem(10)};
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const MapControlButton = styled(IconButton)`
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};
`

const StyledOfferList = styled(OfferList)`
  width: 100%;
`

interface Props {
  listFilters: App.OfferListFilters
  hotelOfferList: App.OfferList
  extras: Array<OfferListV2Item>
  searchId: string
  pageSize: number
  defaultPage: number
  tracking: React.ComponentProps<typeof OfferListEventsProvider>['tracking']
  onListEvent: OfferListEventHandler
  onPageChange: (page: number) => void
  windowSearch: string
  searchDates?: { checkIn?: string; checkOut?: string }
  searchRooms?: Array<App.Occupants>
  selectedOfferIdsParam?: string
  location: Location
  headerHeight: number
  isSearchBlocked: boolean
}

function HomesAndVillasSplitMapView(props: Props) {
  const intl = useIntl()
  const {
    listFilters,
    hotelOfferList,
    extras,
    searchId,
    pageSize,
    defaultPage,
    tracking,
    onListEvent,
    onPageChange,
    windowSearch,
    searchDates,
    searchRooms,
    selectedOfferIdsParam,
    location,
    headerHeight,
    isSearchBlocked,
  } = props

  const dispatch = useAppDispatch()
  const splitMapPaneRef = useRef<HTMLDivElement>(null)
  const mapPaneHeightRef = useRef<number>(0)
  const [mapboxMap, setMapboxMap] = useState<mapboxgl.Map | undefined>(undefined)
  const [fullScreenMap, setFullScreenMap] = useState(false)
  const [mapPaneHeight, setMapPaneHeight] = useState<number | undefined>(undefined)
  const [hoveredOffer, setHoveredOffer] = useState<HoveredOffer | undefined>(undefined)

  useEffect(() => {
    const mapPane = splitMapPaneRef.current
    if (!mapPane) return

    let rafId: number | undefined

    const measureMapPane = () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const rect = mapPane.getBoundingClientRect()
        const newHeight = Math.max(0, Math.round(window.innerHeight - rect.top))
        if (newHeight !== mapPaneHeightRef.current) {
          mapPaneHeightRef.current = newHeight
          setMapPaneHeight(newHeight)
        }
      })
    }

    measureMapPane()
    window.addEventListener('resize', measureMapPane)
    window.addEventListener('scroll', measureMapPane, { passive: true })

    return () => {
      if (rafId !== undefined) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', measureMapPane)
      window.removeEventListener('scroll', measureMapPane)
    }
  }, [headerHeight])

  const handleHover = useCallback((id: string | undefined, type: string | undefined) => {
    if (id && type) {
      setHoveredOffer({ id, type })
    } else {
      setHoveredOffer(undefined)
    }
  }, [])

  const handleListMouseLeave = useCallback(() => {
    setHoveredOffer(undefined)
  }, [])

  const onSplitListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productHover) {
      const { offer } = dispatchAction
      if (isAnyOffer(offer)) {
        handleHover(offer.id, offer.type)
      }
    }
    onListEvent(dispatchAction)
  }, [handleHover, onListEvent])

  const handleMapScreenToggle = useCallback(() => {
    setFullScreenMap(prev => !prev)
  }, [])

  const handleZoomIn = useCallback(() => {
    mapboxMap?.zoomIn()
  }, [mapboxMap])

  const handleZoomOut = useCallback(() => {
    mapboxMap?.zoomOut()
  }, [mapboxMap])

  const onBoundsChanged = useCallback((newBounds: MapBounds) => {
    if (!newBounds) return

    const searchItem: App.SearchItem = {
      searchType: 'bounds',
      value: serialiseMapBounds(newBounds),
      format: {
        mainText: intl.formatMessage({
          id: 'tcVgNBzzpyKGfzPRUaOh',
          defaultMessage: 'Search area',
          description: 'Label for map bounds search area in homes and villas search results',
        }),
      },
    }

    const searchStr = encodeSearchParams({
      urlSearch: windowSearch,
      searchItem,
      dates: {
        checkIn: searchDates?.checkIn,
        checkOut: searchDates?.checkOut,
      },
      rooms: searchRooms,
      filters: listFilters,
      flexibleNights: listFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: listFilters.flexibleMonths,
      userSelectedFlexibleMonths: true,
    }).toString()
    dispatch(replaceWithRegion('/search/homes-and-villas', searchStr))
  }, [windowSearch, searchDates, searchRooms, listFilters, dispatch, intl])

  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(selectedOfferIdsParam).map(({ id }) => id)
  ), [selectedOfferIdsParam])

  const mapOfferListOverride = useMemo<App.OfferList>(() => {
    const pageEnd = defaultPage * pageSize

    return {
      ...hotelOfferList,
      // Keep map pins cumulative as pagination advances, rather than swapping page slices.
      offerIds: hotelOfferList.offerIds.slice(0, pageEnd),
      key: `${hotelOfferList.key}-up-to-page-${defaultPage}`,
    }
  }, [hotelOfferList, defaultPage, pageSize])

  const setSelectedOffersURLParams = useCallback((offers: Array<App.Offer | App.BedbankOffer>) => {
    const newParams = new URLSearchParams(windowSearch)
    if (offers.length > 0) {
      newParams.set('selectedOfferIds', encodeOfferIds(offers))
    } else {
      newParams.delete('selectedOfferIds')
    }
    const newSearchString = newParams.toString()
    if (newSearchString !== windowSearch) {
      dispatch(replace({
        pathname: location.pathname,
        search: newSearchString,
        hash: location.hash,
        state: location.state,
      }))
    }
  }, [dispatch, windowSearch, location.pathname, location.hash, location.state])

  const onDeselectOffers = useCallback(() => {
    setSelectedOffersURLParams([])
  }, [setSelectedOffersURLParams])

  const splitMapPaneStyle = useMemo(() => ({
    '--sticky-top-offset': `${headerHeight}px`,
    ...(mapPaneHeight !== undefined ? { height: `${mapPaneHeight}px` } : {}),
  } as React.CSSProperties), [headerHeight, mapPaneHeight])

  return (
    <CSSBreakpoint min="largeDesktop">
      <div>
        <SplitViewContainer size="full" className={clsx({ hideListView: fullScreenMap })}>
          <SplitListPane gap={16} onMouseLeave={handleListMouseLeave}>
            <OfferListEventsProvider tracking={tracking} onListEvent={onSplitListEvent}>
              <StyledOfferList
                filters={listFilters}
                interstitials={extras}
                onPageChange={onPageChange}
                searchId={searchId}
                pageSize={pageSize}
                tileStyle="search"
                eagerLoadFirstImage
                defaultPage={defaultPage}
              />
            </OfferListEventsProvider>
          </SplitListPane>
          <SplitMapPane ref={splitMapPaneRef} style={splitMapPaneStyle}>
            <MapControlsContainer>
              <MapControlButton
                kind="primary"
                variant="ghost"
                size="medium"
                shape="square"
                onClick={handleMapScreenToggle}
              >
                {fullScreenMap ? <LineTimesIcon /> : <LineExpandAltIcon />}
              </MapControlButton>
              <MapControlButton
                kind="primary"
                variant="ghost"
                size="medium"
                shape="square"
                onClick={handleZoomIn}
              >
                <LinePlusIcon />
              </MapControlButton>
              <MapControlButton
                kind="primary"
                variant="ghost"
                size="medium"
                shape="square"
                onClick={handleZoomOut}
              >
                <LineMinusIcon />
              </MapControlButton>
            </MapControlsContainer>
            <SearchResultsMap
              filters={listFilters}
              onBoundsChanged={onBoundsChanged}
              onSelectOffers={setSelectedOffersURLParams}
              onDeselectOffers={onDeselectOffers}
              selectedOfferIds={selectedOfferIds}
              onError={noop}
              onZeroResults={noop}
              offerListOverride={mapOfferListOverride}
              offerList={hotelOfferList}
              pageSize={pageSize}
              fullScreenMap={fullScreenMap}
              zeroResultsBanner={false}
              isSearchBlocked={isSearchBlocked}
              showPriceOnPins
              hideZoomControl
              disablePinLimit
              onMapReady={setMapboxMap}
              onHover={handleHover}
              externalHoveredOffer={hoveredOffer}
            />
          </SplitMapPane>
        </SplitViewContainer>
      </div>
    </CSSBreakpoint>
  )
}

export default HomesAndVillasSplitMapView
